/**
 * Mapping of product IDs to their route paths
 * This ensures products link to the correct routes as defined in the route configuration files
 *
 * Product IDs are available as global constants from config/config.ts:
 * - MOTOR_PRODUCTS
 * - HEALTH_PRODUCTS
 * - PROPERTY_PRODUCTS
 * - MARINE_PRODUCTS
 */

/**
 * Product ID to route path mapping
 * Maps product IDs to their route paths as defined in config/routes/*.ts files
 * Route paths are relative to /{department}/sales/new/
 */
export const productRouteMapping: { [productId: string]: string } = {
    // Motor products - routes from config/routes/motor.ts
    [MOTOR_PRODUCTS.TPL]: 'tpl',
    [MOTOR_PRODUCTS.CASCO]: 'casco',
    [MOTOR_PRODUCTS.DAILY_CASCO]: 'daily-casco',
    [MOTOR_PRODUCTS.MINI_CASCO]: 'mini-casco',
    [MOTOR_PRODUCTS.FPP]: 'fpp',
    [MOTOR_PRODUCTS.GREEN_CARD]: 'green-card',
    [MOTOR_PRODUCTS.BORDER]: 'border',
    [MOTOR_PRODUCTS.PASSENGER]: 'passenger',
    [MOTOR_PRODUCTS.AUTOSOS]: 'autosos',
    [MOTOR_PRODUCTS.DAILY_AUTOSOS]: 'daily-autosos',
    [MOTOR_PRODUCTS.DRIVER_ACCIDENTS]: 'driver-accidents',

    // Health products - routes from config/routes/health.ts
    [HEALTH_PRODUCTS.PRIVATE]: 'private',
    [HEALTH_PRODUCTS.TRAVEL]: 'travel',
    [HEALTH_PRODUCTS.TRAVEL_ALBANIA]: 'travel-albania',
    [HEALTH_PRODUCTS.ANTITUMOR]: 'antitumor',
    [HEALTH_PRODUCTS.LIFE_PLUS]: 'life-plus',
    [HEALTH_PRODUCTS.TRAVEL_EMBASSY]: 'travel-embassy',
    [HEALTH_PRODUCTS.PERSONAL_ACCIDENTS]: 'personal-accidents',
    [HEALTH_PRODUCTS.TELEHEALTH]: 'telehealth',
    [HEALTH_PRODUCTS.TRAVEL_PLUS]: 'travel-plus',

    // Property products - routes from config/routes/property.ts
    [PROPERTY_PRODUCTS.FIRE]: 'fire',
    [PROPERTY_PRODUCTS.PUBLIC_LIABILITY]: 'public-liability',
    [PROPERTY_PRODUCTS.PUBLIC_LIABILITY_873]: 'public-liability-873',
    [PROPERTY_PRODUCTS.PRODUCT_LIABILITY]: 'product-liability',
    [PROPERTY_PRODUCTS.PROFESSIONAL_LIABILITY]: 'professional-liability',
    [PROPERTY_PRODUCTS.EMPLOYER_LIABILITY]: 'employer-liability',
    [PROPERTY_PRODUCTS.POLLUTION_LIABILITY]: 'pollution-liability',
    [PROPERTY_PRODUCTS.OTHER_LIABILITY]: 'other-liability',
    [PROPERTY_PRODUCTS.BUILDER_LIABILITY_650]: 'builder-liability-650',
    [PROPERTY_PRODUCTS.BUILDER_LIABILITY_693]: 'builder-liability-693',
    [PROPERTY_PRODUCTS.FUEL_LIABILITY]: 'fuel-liability',
    [PROPERTY_PRODUCTS.DIRECTORS_AND_OFFICERS_LIABILITY]: 'directors-and-officers-liability',
    [PROPERTY_PRODUCTS.PROFESSIONAL_HIRING_AGENCY_LIABILITY]: 'professional-hiring-agency-liability',
    [PROPERTY_PRODUCTS.NOTARY]: 'notary',
    [PROPERTY_PRODUCTS.PROFESSIONAL_LIABILITY_873_610]: 'professional-liability-873/610',
    [PROPERTY_PRODUCTS.AGENCY_LIABILITY]: 'agency-liability',
    [PROPERTY_PRODUCTS.PROFESSIONAL_ARCHITECT_ENGINEER]: 'professional-architect-engineer',
    [PROPERTY_PRODUCTS.PROFESSIONAL_INSURANCE_AGENT]: 'professional-insurance-agent',
    [PROPERTY_PRODUCTS.CONTRACT_GUARANTEE]: 'contract-guaranty',
    [PROPERTY_PRODUCTS.OFFER_GUARANTEE]: 'offer-guaranty',
    [PROPERTY_PRODUCTS.LICENSING_GUARANTEE]: 'licensing-guaranty',
    [PROPERTY_PRODUCTS.QUALITY_GUARANTEE]: 'quality-guaranty',
    [PROPERTY_PRODUCTS.MAINTENANCE_GUARANTEE]: 'maintenance-guaranty',
    [PROPERTY_PRODUCTS.CUSTOMS_GUARANTEE]: 'customs-guaranty',
    [PROPERTY_PRODUCTS.CUSTOMS_WAREHOUSE_GUARANTEE]: 'customs-warehouse-guaranty',
    [PROPERTY_PRODUCTS.EXCISE_TAX_GUARANTEE]: 'excise-tax-guaranty',
    [PROPERTY_PRODUCTS.EXCISE_TAX_OTHER_GUARANTEE]: 'excise-tax-other-guaranty',
    [PROPERTY_PRODUCTS.IATA_GUARANTEE]: 'iata-guaranty',
    [PROPERTY_PRODUCTS.BUILDING_PERMIT_GUARANTEE]: 'building-permit-guaranty',
    [PROPERTY_PRODUCTS.WASTE_REMOVAL_GUARANTEE]: 'waste-removal-guaranty',
    [PROPERTY_PRODUCTS.PAYMENT_GUARANTEE]: 'payment-guaranty',
    [PROPERTY_PRODUCTS.CONCESSION_REQUEST_GUARANTEE]: 'concession-request-guaranty',
    [PROPERTY_PRODUCTS.PREPAID_GUARANTEE]: 'prepaid-guaranty',
    [PROPERTY_PRODUCTS.RENT_GUARANTEE]: 'rent-guaranty',
    [PROPERTY_PRODUCTS.JUDICIAL_GUARANTEE]: 'judicial-guaranty',
    [PROPERTY_PRODUCTS.BANK_ANTIGUARANTEE]: 'bank-antiguaranty',
    [PROPERTY_PRODUCTS.PUBLIC_ACCOMMODATING_STRUCTURES]: 'public-accommodating-structures',
    [PROPERTY_PRODUCTS.PUBLIC_NEIGHBOR_LIABILITY]: 'public-neighbor-liability',
    [PROPERTY_PRODUCTS.PROFESSIONAL_LOSS_ASSESSOR_LIABILITY]: 'professional-loss-assessor-liability',
    [PROPERTY_PRODUCTS.PROFESSIONAL_DOCTOR_LIABILITY]: 'professional-doctor-liability',
    [PROPERTY_PRODUCTS.PROFESSIONAL_BROKER_LIABILITY]: 'professional-broker-liability',
    [PROPERTY_PRODUCTS.PROFESSIONAL_LAWYER_LIABILITY]: 'professional-lawyer-liability',
    [PROPERTY_PRODUCTS.PROFESSIONAL_ENFORCEMENT_OFFICER_LIABILITY]: 'professional-enforcement-officer-liability',
    [PROPERTY_PRODUCTS.PUBLIC_PROPERTY_UNDER_PRESSURE_LIABILITY]: 'public-property-under-pressure-liability',
    [PROPERTY_PRODUCTS.PUBLIC_THIRD_PARTY_LIABILITY]: 'public-third-party-liability',
    [PROPERTY_PRODUCTS.EXCISE_TAX_OTHER_IMPORT_GUARANTEE]: 'excise-tax-other-import-guaranty',
    [PROPERTY_PRODUCTS.EMPLOYERS_LIABILITY]: 'employers-liability',
    [PROPERTY_PRODUCTS.PUBLIC_GTPL_PL_EL]: 'public-product-employer-liability',
    [PROPERTY_PRODUCTS.PROFESSIONAL_REALESTATEAPPRAISER_LIABILITY]: 'professional-real-estate-appraiser-liability',
    [PROPERTY_PRODUCTS.CYBER]: 'cyber',
    [PROPERTY_PRODUCTS.DO]: 'do',
    [PROPERTY_PRODUCTS.CIT]: 'banking/cit',
    [PROPERTY_PRODUCTS.CASH_VAULT]: 'banking/cash-vault',
    [PROPERTY_PRODUCTS.LIVESTOCK_INSURANCE]: 'livestock-insurance',
    [PROPERTY_PRODUCTS.CROP_INSURANCE]: 'crop-insurance',
    [PROPERTY_PRODUCTS.GREENHOUSE_INSURANCE]: 'greenhouse-insurance',
    [PROPERTY_PRODUCTS.DEVICE_INSURANCE]: 'device-insurance',

    // Marine products - routes from config/routes/marine.ts
    [MARINE_PRODUCTS.MVL]: 'mvl',
    [MARINE_PRODUCTS.CARGO_IN_TRANSIT]: 'cargo-in-transit',
    [MARINE_PRODUCTS.CMR]: 'cmr',
    [MARINE_PRODUCTS.HM]: 'hm',
    [MARINE_PRODUCTS.PI]: 'pi',
    [MARINE_PRODUCTS.AVIATION_CASCO]: 'aviation-casco',
    [MARINE_PRODUCTS.AVIATION_TPL]: 'aviation-tpl',
};

/**
 * Reverse mapping: route path to product ID
 * Used to extract product ID from current route
 */
export const routeToProductMapping: { [routePath: string]: string } = Object.entries(productRouteMapping).reduce((acc, [productId, routePath]) => {
    acc[routePath] = productId;
    return acc;
}, {} as { [routePath: string]: string });

/**
 * Extract product ID from route pathname
 * @param pathname - Current route pathname (e.g., '/motor/sales/new/tpl')
 * @returns Product ID or null if not a product page
 */
export const getProductIdFromRoute = (pathname: string): string | null => {
    // Match pattern: /{department}/sales/new/{product-route}
    const match = pathname.match(/\/(motor|health|property|marine)\/sales\/new\/(.+)$/);
    if (!match) return null;

    const routePath = match[2]; // Extract the product route part
    return routeToProductMapping[routePath] || null;
};

/**
 * Sale page path used by the Rinovo / renewal redirect.
 * Example: /property/sales/new/fire
 */
export const getProductSalePath = (insuranceSectorType: string, productId: string): string | null => {
    const productRoute = productRouteMapping[productId];
    if (!productRoute) return null;

    return `/${String(insuranceSectorType || '').toLocaleLowerCase()}/sales/new/${productRoute}`;
};
